import { createTheme } from "@mui/material/styles";
import type { ColorMode } from "../contexts/ColorModeContext";

const sharedTypography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h3: { fontSize: "2rem", fontWeight: 600, lineHeight: 1.4 },
  h4: { fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: "1.25rem", fontWeight: 500 },
  h6: { fontSize: "1rem", fontWeight: 500 },
  body1: { fontSize: "1rem", lineHeight: 1.6 },
  body2: { fontSize: "0.875rem", lineHeight: 1.6 },
  button: { textTransform: "none" as const, fontWeight: 500 },
};

const sharedComponents = {
  MuiButton: {
    styleOverrides: {
      root: { borderRadius: 8, padding: "10px 24px" },
      contained: { boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: 12 },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: { boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)" },
    },
  },
};

export function createAppTheme(mode: ColorMode) {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? "#7c9cff" : "#1a237e",
        light: isDark ? "#a8b8ff" : "#534bae",
        dark: isDark ? "#4a5eb5" : "#000051",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#00b0ff",
        light: "#69e2ff",
        dark: "#0081cb",
        contrastText: isDark ? "#000000" : "#000000",
      },
      background: {
        default: isDark ? "#0f1117" : "#f5f7fa",
        paper: isDark ? "#1a1d26" : "#ffffff",
      },
      text: {
        primary: isDark ? "#e8eaed" : "#2d3748",
        secondary: isDark ? "#9aa0a6" : "#4a5568",
      },
      divider: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
      success: { main: "#4caf50" },
      warning: { main: "#ff9800" },
      error: { main: "#f44336" },
      info: { main: "#2196f3" },
    },
    typography: {
      ...sharedTypography,
      h1: {
        fontSize: "3.5rem",
        fontWeight: 700,
        lineHeight: 1.2,
        color: isDark ? "#c5cae9" : "#1a237e",
      },
      h2: {
        fontSize: "2.5rem",
        fontWeight: 600,
        lineHeight: 1.3,
        color: isDark ? "#c5cae9" : "#1a237e",
      },
    },
    shape: { borderRadius: 8 },
    components: {
      ...sharedComponents,
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isDark
              ? "0 4px 12px rgba(0, 0, 0, 0.4)"
              : "0 4px 12px rgba(0, 0, 0, 0.08)",
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: isDark ? "#5f6368 #1a1d26" : "#888 #f1f1f1",
          },
        },
      },
    },
  });
}
